import classNames from 'classnames';
import React, { useRef } from 'react';
import { DropZoneComponent } from 'src/common/components/drop-zone/component';
import FileDropzone from 'src/common/FileDropzone';
import { useFilesUploadStore } from 'src/common/store/files-upload.store';
import { UploadCloudLightIcon } from 'src/monet/icons';
import { Nullable } from 'src/types/nullable.type';
import { WizardContentSectionTemplate } from './template';
import delay from 'lodash/delay';
import { REMOVE_STACK_DELAY_IN_MS } from 'src/dashboard/library/content';
import { useWizardStore } from '../../store';
import { AssetResponse } from 'src/common/helpers/file-upload';
import { getFileInlineMetadata } from 'src/common/helpers/get-file-inline-metadata';
import { useUserOnboardingStore } from 'src/onboarding/store/user-onboarding.store';
import { getContentParamsByAsset } from 'src/dashboard/library/content/helpers/get-content-params-by-asset';

export interface FileWithMeta {
  file: File;
  meta: string;
}

export type UploadAssetType = { asset: AssetResponse; meta?: object };

interface Props {
  onSelectFiles: (files: FileWithMeta[]) => void;
}

export const WizardContentSectionUploadFile = ({ onSelectFiles }: Props) => {
  const inputFileElementRef = useRef<Nullable<HTMLInputElement>>(null);
  const { addUploadStack, removeUploadStack, startUpload } = useFilesUploadStore();
  const { setUploadingBubbleShow, setTip } = useUserOnboardingStore((state) => ({
    setUploadingBubbleShow: state.setShow,
    setTip: state.setTip,
  }));
  const { increaseWizardOngoingUploadCount, decreaseOngoingUploadCount, stackNodeCreation } = useWizardStore(
    (state) => ({
      increaseWizardOngoingUploadCount: state.increaseOngoingUploadCount,
      decreaseOngoingUploadCount: state.decreaseOngoingUploadCount,
      stackNodeCreation: state.stackNodeCreation,
    })
  );

  const onFilesDropped = async (files: File[]) => {
    setTip();
    const uploadedAssets: UploadAssetType[] = [];
    const { length: pendingUploadLength } = files;
    const filesWithMeta = await Promise.all(
      files.map(async (file) => ({ file, meta: await getFileInlineMetadata(file) }))
    );

    new Promise<UploadAssetType[]>((resolve) => {
      increaseWizardOngoingUploadCount(filesWithMeta.length);
      addUploadStack(files);
      onSelectFiles(filesWithMeta);
      setUploadingBubbleShow(true);
      startUpload({
        onSuccess: (asset, meta) => {
          decreaseOngoingUploadCount();
          uploadedAssets.push({ asset, meta });
          if (pendingUploadLength === uploadedAssets.length) {
            resolve(uploadedAssets);
          }
          delay(() => {
            removeUploadStack(asset.name);
          }, REMOVE_STACK_DELAY_IN_MS);
        },
        onCancel: (name) => {
          decreaseOngoingUploadCount();
          removeUploadStack(name);
        },
      });
    }).then((assets) => {
      const assetPayloads = assets.map(({ asset, meta }) => ({ ...meta, ...getContentParamsByAsset(asset) }));
      assetPayloads.forEach(({ contentType, payload }) => {
        stackNodeCreation(contentType, payload);
      });
    });
  };

  const onDropZoneClicked = () => {
    if (inputFileElementRef.current) {
      inputFileElementRef.current.click();
    }
  };
  return (
    <>
      <WizardContentSectionTemplate
        title='Files'
        description='If your content is a PDF, spreadsheet, or any file, upload it.'
      >
        <DropZoneComponent containerClassName='h-[7.5rem]' onFileDrop={onFilesDropped}>
          {(dragging) => (
            <div className='relative w-full'>
              <div
                className={classNames('w-full h-full transition-opacity', {
                  'opacity-0': dragging,
                  'opacity-100': !dragging,
                })}
              />
              <div
                onClick={onDropZoneClicked}
                className={classNames(
                  'absolute gap-2 inset-0 flex items-center justify-center',
                  'cursor-pointer text-bedrock-p px-3 w-full',
                  'transition-colors rounded-lg border-dashed border-2',
                  {
                    'border-bedrock-brand-secondary bg-bedrock-brand-secondary/transparent text-bedrock-brand-secondary':
                      dragging,
                    'border-bedrock-gray-medium bg-bedrock-light-gray text-bedrock-black': !dragging,
                  }
                )}
              >
                <UploadCloudLightIcon className='shrink-0' />
                <span>{dragging ? <>Drop it, like its hot</> : <>Drag and drop or click to upload</>}</span>
              </div>
            </div>
          )}
        </DropZoneComponent>
      </WizardContentSectionTemplate>
      <FileDropzone
        className='hide'
        type='file'
        copy={null}
        multiple
        onFilesUpdate={onFilesDropped}
        resetOnSuccess
        dropzoneClasses={null}
        inputRef={inputFileElementRef}
      />
    </>
  );
};
